import './EntityBulkTagTab.scss';

import { caseInsensitiveContainsFilterOption } from '@lib/filter';
import debounce from 'lodash/debounce';
import { haltClickEventPropagation } from '@lib/events';
import { PaginationQueryParams } from '@periscopes/general';
import { SonarTag } from '@sonar-software/sonar-core';

import { ApiCodeToggleQueryParams, SearchFilterQueryParams, TagFilterQueryParams } from '@periscopes/general/filters';
import { Button, Select, Space, Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { COPY_SPECIFIC, PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import { OffsetPaginatedResource, PeriscopeSfField } from '@sonar-software/sonar-ts-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useLinkTag, useQueryResource, useTags } from '@hooks';

type Resource = OffsetPaginatedResource<PeriscopeSfField>;
export type EntityBulkTagTabQueryParams = PaginationQueryParams &
	TagFilterQueryParams &
	SearchFilterQueryParams &
	ApiCodeToggleQueryParams;

type CheckFieldResource = FieldId[];
export type CheckFieldsQueryParams = { fieldInternalNames: string[] };

interface CustomTagProps {
	label: React.ReactNode;
	value: string;
	disabled: boolean;
	onClose: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
	closable: boolean;
}

export type FieldId = {
	internalName: string;
	sonarObjectId: number;
};

export interface EntityBulkTagTabsProps {
	sonarObjectId: number;
}

export const EntityBulkTagTab: React.FC<EntityBulkTagTabsProps> = ({ sonarObjectId }) => {
	const fieldsUri = `v2/salesforce/SfEntityDefinitions/${sonarObjectId}/fields`;
	const checkUri = `v2/salesforce/SfEntityDefinitions/${sonarObjectId}/check-fields`;
	const [ selectedFields, setSelectedFields ] = useState<string[]>();
	const [ selectedTag, setSelectedTag ] = useState<number>();

	const tags = useTags(true);
	const { request: linkTag } = useLinkTag();

	const { loading, resource, updateParams } = useQueryResource<Resource, EntityBulkTagTabQueryParams>({
		defaultParams: {
			count: 8,
			offset: 0
		},
		uri: fieldsUri
	});

	const {
		loading: checking,
		resource: fieldIdMatches,
		updateParams: updateFieldCheckParams
	} = useQueryResource<CheckFieldResource, CheckFieldsQueryParams>({
		defaultParams: { fieldInternalNames: [] },
		uri: checkUri
	});

	const fieldSuggestions = resource?.results ?? [];

	const debouncedUpdateParams = useMemo(() => debounce(updateParams, 300), [ updateParams ]);

	const handleSearchChange = useCallback(
		(query: string) => debouncedUpdateParams({ query }),
		[ debouncedUpdateParams ]
	);

	const resetForm = useCallback(() => {
		setSelectedFields([]);
		setSelectedTag(undefined);
	}, []);

	const handleBulkTagClick = useCallback(() => {
		if (!selectedTag) {
			return;
		}

		linkTag({
			sonarObjectIds: fieldIdMatches?.map((item) => item.sonarObjectId) ?? [],
			sonarObjectTagId: selectedTag
		});

		resetForm();
	}, [ fieldIdMatches, linkTag, resetForm, selectedTag ]);

	const tagRender = useCallback(
		(tagProps: CustomTagProps) => {
			const { label, value, closable, onClose } = tagProps;
			const found = (fieldIdMatches?.findIndex((match) => match.internalName === value) ?? -1) > -1;
			const statusIcon = found ? <CheckCircleOutlined /> : <CloseCircleOutlined />;
			const processing = !found && checking;

			return (
				<Tag
					className='field-name-tag'
					closable={ closable }
					color={ found ? 'blue' : '' }
					icon={ processing ? <SyncOutlined spin /> : statusIcon }
					onClose={ onClose }
					onMouseDown={ haltClickEventPropagation }>
					{ label }
				</Tag>
			);
		},
		[ fieldIdMatches, checking ]
	);

	return (
		<div className='bulk-tag'>
			<Space direction='vertical'>
				<Select
					allowClear
					className='field-select'
					filterOption={ caseInsensitiveContainsFilterOption }
					loading={ loading || checking }
					mode='tags'
					onChange={ (fieldInternalNames) => updateFieldCheckParams({ fieldInternalNames }) }
					onSearch={ handleSearchChange }
					options={ fieldSuggestions.map((field) => ({
						label: field.internalName,
						value: field.internalName
					})) }
					placeholder={ PLACEHOLDERS_COPY.PASTE_FIELD_NAMES }
					tagRender={ tagRender }
					tokenSeparators={ [ ', ', '\n', '\r' ] }
					value={ selectedFields } />
				<Space>
					<Select
						onChange={ (id) => setSelectedTag(Number(id)) }
						options={ tags.filter((tag) => !tag.autoTagged)
							.map((tag: SonarTag) => ({
								label: tag.name,
								value: tag.id
							})) }
						placeholder={ COPY_SPECIFIC.SELECT_A_TAG }
						style={ { width: 200 } }
						value={ selectedTag } />
					<Button
						disabled={ !selectedTag || !fieldIdMatches?.length }
						onClick={ handleBulkTagClick }
						type='primary'>
						{ COPY_SPECIFIC.BULK_TAG }
					</Button>
				</Space>
			</Space>
		</div>
	);
};
